// node modules
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Lightbox from 'react-images';
import withStyles from 'isomorphic-style-loader/withStyles';

// app modules: components
import Button from 'components/Button';
import Heading from 'components/Heading';
import InstagramModal from 'components/InstagramModal';

// app modules: props
import RecipeImageProps from 'components/RecipeImage/RecipeImageProps';

// app modules: services
import { getImageSrc, getImageSrcSet } from 'services/image';

// assets
import CssRecipeGallery from './RecipeGallery.scss';

const LIMIT = 8;
const THUMBNAIL_HEIGHT = 105;

class RecipeGallery extends Component {
  static propTypes = {
    images: RecipeImageProps.graphics,
    instagramHtml: PropTypes.string,
    instagramWidth: PropTypes.number,
  };

  static defaultProps = {
    images: [],
    instagramHtml: null,
    instagramWidth: null,
  };

  constructor(props) {
    super(props);

    this.state = {
      isOpen: false,
      currentImage: 0,
      showAll: false,
    };

    this.bound = {
      toggleShowAll: this.toggleShowAll.bind(this),
      openLightbox: this.openLightbox.bind(this),
      closeLightbox: this.closeLightbox.bind(this),
      gotoPrevious: this.gotoPrevious.bind(this),
      gotoNext: this.gotoNext.bind(this),
    };
  }

  toggleShowAll() {
    const { showAll } = this.state;
    this.setState({
      showAll: !showAll,
    });
  }

  openLightbox(index, event) {
    event.preventDefault();
    this.setState({
      currentImage: index,
      isOpen: true,
    });
  }

  closeLightbox() {
    this.setState({
      currentImage: 0,
      isOpen: false,
    });
  }

  gotoPrevious() {
    this.setState({
      currentImage: this.state.currentImage - 1,
    });
  }

  gotoNext() {
    this.setState({
      currentImage: this.state.currentImage + 1,
    });
  }

  renderImages() {
    const { showAll } = this.state;
    const { images } = this.props;
    const limit = showAll ? images.length : LIMIT;
    return images
      .filter(image => !!image.crops?.length)
      .slice(0, limit)
      .map((image, imageIndex) => (
        <div className="recipe-gallery__item" key={image.src}>
          <button onClick={event => this.bound.openLightbox(imageIndex, event)}>
            <figure className="recipe-gallery__image">
              <img
                src={getImageSrc(image, 'square', size => size.w >= 100)}
                srcSet={getImageSrcSet(image, 'square', [{ x: 1, w: 100 }, { x: 2, w: 200 }])}
                width={THUMBNAIL_HEIGHT}
                height={THUMBNAIL_HEIGHT}
                alt={image.caption}
                loading="lazy"
              />
            </figure>
          </button>
        </div>
      ));
  }

  renderToggleButton() {
    const { images } = this.props;
    const { showAll } = this.state;

    if (images.length > LIMIT) {
      return (
        <Button
          themeName="energized"
          themeType="clear"
          size="large"
          onClick={this.bound.toggleShowAll}
        >
          {showAll ? 'Hide' : `Show all (${images.length})`}
        </Button>
      );
    }

    return null;
  }

  render() {
    const { images, instagramHtml, instagramWidth, ...more } = this.props;
    const { currentImage, isOpen } = this.state;

    const lightboxImages = images.filter(image => !!image.crops?.length).map(image => {
      if (image.type === 'instagram') {
        return image;
      }
      return {
        src: getImageSrc(image, 'original', size => size.w >= 800, { getLargestInstead: true }),
        srcset: getImageSrcSet(image, 'original', size => size.w >= 800),
        caption: image.caption,
      };
    });
    const instagramSelected = lightboxImages[currentImage]?.type === 'instagram';
    let lightbox = null;
    if (isOpen) {
      if (instagramSelected && instagramHtml) {
        lightbox = (
          <InstagramModal
            html={instagramHtml}
            maxWidth={instagramWidth}
            onClose={this.bound.closeLightbox}
            open={isOpen}
          />
        );
      } else {
        lightbox = (
          <Lightbox
            backdropClosesModal
            currentImage={currentImage}
            images={lightboxImages}
            isOpen
            onClickNext={this.bound.gotoNext}
            onClickPrev={this.bound.gotoPrevious}
            onClose={this.bound.closeLightbox}
            showImageCount={false}
          />
        );
      }
    }
    return (
      <div className="recipe-gallery__container">
        <Heading className="margin-bottom-20">
          Photos
          {this.renderToggleButton()}
        </Heading>
        <div className="recipe-gallery" {...more}>
          {this.renderImages()}
        </div>

        {lightbox}
      </div>
    );
  }
}

export default withStyles(CssRecipeGallery)(RecipeGallery);
